import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TemplateManager = ({ templates, onRefresh, onTemplateCreate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const filteredTemplates = templates?.filter(template => {
    if (filterType !== 'all' && template?.type !== filterType) return false;
    if (filterCategory !== 'all' && template?.category !== filterCategory) return false;
    return true;
  });

  const getTypeIcon = (type) => {
    const iconMap = {
      whatsapp: 'MessageCircle',
      email: 'Mail',
      sms: 'MessageSquare',
      in_app: 'Bell'
    };
    return iconMap?.[type] || 'FileText';
  };

  const getTypeColor = (type) => {
    const colorMap = {
      whatsapp: 'text-green-600 bg-green-500/10',
      email: 'text-blue-600 bg-blue-500/10',
      sms: 'text-purple-600 bg-purple-500/10',
      in_app: 'text-orange-600 bg-orange-500/10'
    };
    return colorMap?.[type] || 'text-gray-600 bg-gray-500/10';
  };

  return (
    <div className="bg-card rounded-xl p-4 sm:p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Icon name="FileText" size={24} className="mr-3 text-primary" />
          <h2 className="text-xl font-semibold text-foreground">Message Templates</h2>
        </div>
        <Button iconName="RefreshCw" variant="outline" size="sm" onClick={onRefresh}>
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Type
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e?.target?.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Types</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="in_app">In-App</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Category
          </label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e?.target?.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Categories</option>
            <option value="general">General</option>
            <option value="lead_nurturing">Lead Nurturing</option>
            <option value="appointment">Appointment</option>
            <option value="commission">Commission</option>
            <option value="marketing">Marketing</option>
          </select>
        </div>
      </div>

      {/* Templates List */}
      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {filteredTemplates?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No templates found</p>
            <p className="text-sm text-muted-foreground mt-2">Create your first template to get started</p>
          </div>
        ) : (
          filteredTemplates?.map((template) => (
            <div
              key={template?.id}
              onClick={() => setSelectedTemplate(template)}
              className="p-4 bg-muted/20 rounded-lg border border-border hover:border-primary cursor-pointer transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${getTypeColor(template?.type)}`}>
                    <Icon name={getTypeIcon(template?.type)} size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground truncate">
                      {template?.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded capitalize">
                        {template?.category?.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {template?.language?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0" />
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {template?.content}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Template Detail Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-xl p-6 max-w-2xl w-full border border-border max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">{selectedTemplate?.name}</h2>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 ${getTypeColor(selectedTemplate?.type)}`}>
                  <Icon name={getTypeIcon(selectedTemplate?.type)} size={16} />
                  <span className="text-sm font-medium capitalize">{selectedTemplate?.type}</span>
                </div>
                <div className="px-3 py-1.5 bg-muted rounded-lg text-sm text-muted-foreground capitalize">
                  {selectedTemplate?.category?.replace('_', ' ')}
                </div>
              </div>

              {selectedTemplate?.subject && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground block mb-2">
                    Subject
                  </label>
                  <p className="text-sm text-foreground p-3 bg-muted/20 rounded-lg">
                    {selectedTemplate?.subject}
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-muted-foreground block mb-2">
                  Content
                </label>
                <p className="text-sm text-foreground p-4 bg-muted/20 rounded-lg whitespace-pre-wrap">
                  {selectedTemplate?.content}
                </p>
              </div>

              {selectedTemplate?.variables?.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground block mb-2">
                    Variables
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate?.variables?.map((variable, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary/10 text-primary rounded text-xs font-mono"
                      >
                        {`{{${variable}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setSelectedTemplate(null)}>
                Close
              </Button>
              <Button fullWidth iconName="Copy">
                Duplicate Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManager;